import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useState, type FormEvent } from "react";
import { z } from "zod";
import { toast } from "sonner";
import { authErrorKey } from "@/lib/auth-error";
import { supabase } from "@/integrations/supabase/client";
import { SiteHeader } from "@/components/site-header";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { LegalConsent } from "@/components/legal-consent";
import { useI18n } from "@/lib/i18n";


export const Route = createFileRoute("/signup")({
  head: () => ({
    meta: [
      { title: "Üyelik başvurusu — JUËN" },
      { name: "description", content: "JUËN üyelik başvurusu yapın." },
    ],
  }),
  component: SignupPage,
});

const BLOCKED_EMAIL_DOMAINS = [
  "spamtest.com",
  "mailinator.com",
  "tempmail.com",
  "10minutemail.com",
  "guerrillamail.com",
  "trashmail.com",
  "yopmail.com",
  "throwawaymail.com",
  "fakeinbox.com",
  "getnada.com",
  "dispostable.com",
  "maildrop.cc",
  "sharklasers.com",
];

const schema = z.object({
  first_name: z.string().trim().min(1, "Ad gerekli").max(50),
  last_name: z.string().trim().min(1, "Soyad gerekli").max(50),
  email: z
    .string()
    .trim()
    .toLowerCase()
    .email("Geçerli e-posta giriniz")
    .max(255)
    .refine(
      (v) => {
        const domain = v.split("@")[1] ?? "";
        return !BLOCKED_EMAIL_DOMAINS.includes(domain);
      },
      "Bu e-posta sağlayıcısı kabul edilmiyor",
    ),
  password: z.string().min(6, "Şifre en az 6 karakter"),
  phone: z.string().trim().min(7, "Telefon gerekli").max(20),
  birth_date: z.string().min(1, "Doğum tarihi gerekli").refine((v) => {
    const d = new Date(v);
    if (isNaN(d.getTime())) return false;
    const now = new Date();
    let age = now.getFullYear() - d.getFullYear();
    const m = now.getMonth() - d.getMonth();
    if (m < 0 || (m === 0 && now.getDate() < d.getDate())) age--;
    return age >= 18;
  }, "18 yaşından küçükler üyelik başvurusu yapamaz"),
  instagram: z.string().trim().max(50).optional().or(z.literal("")),
  city: z.string().trim().min(1, "Şehir gerekli").max(50),
  gender: z.enum(["male", "female"], { message: "Cinsiyet seçiniz" }),
  bio: z.string().trim().max(500).optional().or(z.literal("")),
});

function SignupPage() {
  const { t } = useI18n();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [legalAccepted, setLegalAccepted] = useState(false);
  const [form, setForm] = useState({
    first_name: "",
    last_name: "",
    email: "",
    password: "",
    phone: "",
    birth_date: "",
    instagram: "",
    city: "",
    gender: "",
    bio: "",
  });

  const update = (k: keyof typeof form) => (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) =>
    setForm((s) => ({ ...s, [k]: e.target.value }));

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (!legalAccepted) {
      toast.error(t("signup.legalRequired"));
      return;
    }
    const parsed = schema.safeParse(form);
    if (!parsed.success) {
      toast.error(parsed.error.issues[0]?.message ?? t("signup.checkFields"));
      return;
    }

    setLoading(true);
    const { email, password, ...meta } = parsed.data;
    const { error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        emailRedirectTo: `${window.location.origin}/dashboard`,
        data: meta,
      },
    });
    setLoading(false);

    if (error) {
      toast.error(t(authErrorKey(error.message)));
      return;
    }

    // Admin signup notification is dispatched server-side via a database trigger
    // (see notify_admin_new_signup on public.profiles).

    toast.success(t("signup.received"));
    void navigate({ to: "/dashboard" });
  };

  return (
    <div className="min-h-screen bg-background">
      <SiteHeader />
      <div className="mx-auto max-w-2xl px-4 py-10">
        <div className="mb-8 text-center">
          <h1 className="text-3xl font-bold tracking-tight md:text-4xl">{t("signup.title")}</h1>
          <p className="mt-2 text-sm text-muted-foreground">
            {t("signup.subtitle")}
          </p>
        </div>

        <form
          onSubmit={handleSubmit}
          className="space-y-5 rounded-2xl border border-border bg-card p-6 shadow-card md:p-8"
        >
          <div className="grid gap-4 md:grid-cols-2">
            <div className="space-y-2">
              <Label htmlFor="first_name">{t("signup.firstName")} *</Label>
              <Input id="first_name" value={form.first_name} onChange={update("first_name")} required />
            </div>
            <div className="space-y-2">
              <Label htmlFor="last_name">{t("signup.lastName")} *</Label>
              <Input id="last_name" value={form.last_name} onChange={update("last_name")} required />
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="email">{t("signup.email")} *</Label>
            <Input id="email" type="email" value={form.email} onChange={update("email")} required />
          </div>

          <div className="space-y-2">
            <Label htmlFor="password">{t("signup.password")} *</Label>
            <Input id="password" type="password" value={form.password} onChange={update("password")} required />
            <p className="text-xs text-muted-foreground">{t("signup.passwordHint")}</p>
          </div>

          <div className="grid gap-4 md:grid-cols-2">
            <div className="space-y-2">
              <Label htmlFor="phone">{t("signup.phone")} *</Label>
              <Input id="phone" type="tel" value={form.phone} onChange={update("phone")} required />
            </div>
            <div className="space-y-2">
              <Label htmlFor="birth_date">{t("signup.birthDate")} *</Label>
              <Input
                id="birth_date"
                type="date"
                value={form.birth_date}
                onChange={update("birth_date")}
                max={new Date(new Date().setFullYear(new Date().getFullYear() - 18)).toISOString().split("T")[0]}
                required
              />
            </div>
          </div>

          <div className="grid gap-4 md:grid-cols-2">
            <div className="space-y-2">
              <Label htmlFor="city">{t("signup.city")} *</Label>
              <Input id="city" value={form.city} onChange={update("city")} required />
            </div>
            <div className="space-y-2">
              <Label htmlFor="instagram">Instagram</Label>
              <Input id="instagram" placeholder={t("signup.instagramPlaceholder")} value={form.instagram} onChange={update("instagram")} />
            </div>
          </div>

          <div className="space-y-2">
            <Label>{t("signup.gender")} *</Label>
            <RadioGroup
              value={form.gender}
              onValueChange={(v) => setForm((s) => ({ ...s, gender: v }))}
              className="flex gap-6"
            >
              <div className="flex items-center gap-2">
                <RadioGroupItem id="gender-female" value="female" />
                <Label htmlFor="gender-female" className="cursor-pointer font-normal">{t("signup.female")}</Label>
              </div>
              <div className="flex items-center gap-2">
                <RadioGroupItem id="gender-male" value="male" />
                <Label htmlFor="gender-male" className="cursor-pointer font-normal">{t("signup.male")}</Label>
              </div>
            </RadioGroup>
          </div>

          <LegalConsent checked={legalAccepted} onCheckedChange={setLegalAccepted} />

          <Button type="submit" disabled={loading || !legalAccepted} className="h-12 w-full text-base">
            {loading ? t("signup.sending") : t("signup.submit")}
          </Button>

          <p className="text-center text-sm text-muted-foreground">
            {t("signup.haveAccount")}{" "}
            <Link to="/login" className="font-medium text-primary hover:underline">
              {t("signup.loginLink")}
            </Link>
          </p>
        </form>
      </div>
    </div>
  );
}
